/**
 * Shared timeline helpers — time formatting, entry grouping, token formatting.
 */

import { parseActivityMessage } from "./activity-parsing";
import type { TimelineEntry, GroupedTimelineItem } from "./timeline-types";

export function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}K`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

// How far ahead we look for a matching `Tool X returned` row when pairing a
// `Calling tool X`. 16 covers normal back-to-back tool runs without folding
// across unrelated chunks of the feed.
const TOOL_PAIR_LOOKAHEAD = 16;

export function groupEntries(entries: TimelineEntry[]): GroupedTimelineItem[] {
  const items: GroupedTimelineItem[] = [];
  // Activity-row indices that have been folded into a `tool_pair` and so
  // should be skipped when we encounter them in the main walk.
  const consumedResults = new Set<number>();

  let i = 0;
  while (i < entries.length) {
    const entry = entries[i];

    if (entry.type === "activity") {
      const parsed = parseActivityMessage(entry.message);

      if (parsed.kind === "tool_result" && consumedResults.has(i)) {
        i++;
        continue;
      }

      if (parsed.kind === "tool_call") {
        let matchIdx: number | undefined;
        const stop = Math.min(entries.length, i + 1 + TOOL_PAIR_LOOKAHEAD);
        for (let j = i + 1; j < stop; j++) {
          if (consumedResults.has(j)) continue;
          const candidate = entries[j];
          if (candidate.type !== "activity") continue;
          const cp = parseActivityMessage(candidate.message);
          if (
            cp.kind === "tool_result" &&
            cp.agent === parsed.agent &&
            cp.toolName === parsed.toolName
          ) {
            matchIdx = j;
            break;
          }
        }
        const matched =
          matchIdx !== undefined
            ? (entries[matchIdx] as Extract<
                TimelineEntry,
                { type: "activity" }
              >)
            : undefined;
        if (matchIdx !== undefined) consumedResults.add(matchIdx);

        items.push({
          kind: "tool_pair",
          agent: parsed.agent,
          toolName: parsed.toolName,
          callTimestamp: entry.timestamp,
          resultTimestamp: matched?.timestamp,
          status: matched ? "completed" : "pending",
          key: `tool-${parsed.agent}-${parsed.toolName}-${entry.timestamp}-${entry.key}`,
        });
        i++;
        continue;
      }

      items.push({ kind: "single", entry });
      i++;
      continue;
    }

    if (entry.type !== "agent_message") {
      items.push({ kind: "single", entry });
      i++;
      continue;
    }

    const group: Extract<TimelineEntry, { type: "agent_message" }>[] = [entry];
    let j = i + 1;
    while (
      j < entries.length &&
      entries[j].type === "agent_message" &&
      (entries[j] as Extract<TimelineEntry, { type: "agent_message" }>)
        .source === entry.source
    ) {
      group.push(
        entries[j] as Extract<TimelineEntry, { type: "agent_message" }>,
      );
      j++;
    }

    items.push({
      kind: "message_group",
      source: entry.source,
      sourceLabel: entry.sourceLabel,
      entries: group,
      firstTimestamp: group[0].timestamp,
      lastTimestamp: group[group.length - 1].timestamp,
      hasStreaming: group.some((m) => m.streaming),
      key: `group-${entry.source}-${group[0].key}`,
    });

    i = j;
  }

  return items;
}
